import _ from 'lodash';
import { build } from '@/api/amplify/graphql/types/settings';
import { getAccountSettings, getUserSettings, setAccountSettings, setUserSettings } from '@/api/legacy/settings';
import { ns } from '@/helpers/store';
import StoreCache from '@/store/cache';
import { FETCH_SETTINGS, UPDATE_LEGACY_SETTINGS, UPDATE_SETTINGS } from '@/store/types/actions';
import { ACCOUNT_SETTINGS_FETCHED, PERSISTENCE_ACCOUNT_SET, SETTINGS_FETCHED, USER_SETTINGS_FETCHED } from '@/store/types/mutations';
import { PERSISTENCE } from '@/store/types/namespaces';

// Shorter lifespan for account cache because this is more likely to be updated by another user
const accountCache = new StoreCache({ lifespan: 10000 });
const userCache = new StoreCache();

function accountLegacyToState(legacy = {}) {
  return {
    emblem: legacy.navigationEmblem,
    logo: legacy['ui.dashboard.logoUrl'],
    title: legacy['ui.title'],
    favicon: legacy['ui.favicon'],
    colours: legacy['ui.colours'],
    brandColour: legacy['ui.brandColour'],
  };
}
function updateLegacyWithForm(legacy, form) {
  const settings = {
    navigationEmblem: form.emblem,
    'ui.dashboard.logoUrl': form.logo,
    'ui.title': form.title,
    'ui.favicon': form.favicon,
    'ui.brandColour': form.brandColour,
  };
  return _.merge({}, legacy, settings);
}

// actions
const actions = {
  [FETCH_SETTINGS]({ state, commit }) {
    let userPromise;
    if (userCache.isReady()) {
      userPromise = Promise.resolve(state.legacy.userSettings);
    } else if (userCache.isPending()) {
      userPromise = userCache.getPendingPromise();
    } else {
      userPromise = userCache
        .setPendingPromise(getUserSettings()
          .then((settings) => {
            commit(USER_SETTINGS_FETCHED, { settings });
          }));
    }

    let accountPromise;
    if (accountCache.isReady()) {
      accountPromise = Promise.resolve(state.legacy.accountSettings);
    } else if (accountCache.isPending()) {
      accountPromise = accountCache.getPendingPromise();
    } else {
      accountPromise = accountCache
        .setPendingPromise(getAccountSettings()
          .then((settings) => {
            commit(ACCOUNT_SETTINGS_FETCHED, { settings });
          }));
    }

    return Promise.all([userPromise, accountPromise])
      .then(() => commit(
        ns(PERSISTENCE, PERSISTENCE_ACCOUNT_SET),
        state.legacy,
        { root: true },
      ));
  },
  [UPDATE_SETTINGS]({ state, commit }, form) {
    const transformed = updateLegacyWithForm(state.legacy.accountSettings || {}, form);
    return setAccountSettings(transformed)
      .then((settings) => commit(ACCOUNT_SETTINGS_FETCHED, { settings }));
  },

  [UPDATE_LEGACY_SETTINGS]({ state, commit }, form) {
    const updated = {
      ...state.legacy.userSettings,
      ...form,
    };
    return setUserSettings(updated, Object.keys(form))
      .then((settings) => commit(USER_SETTINGS_FETCHED, { settings }));
  },
};

// mutations
const mutations = {
  [SETTINGS_FETCHED]() {
    throw new Error('SETTINGS_FETCHED Not implemented.');
  },

  // retired
  [ACCOUNT_SETTINGS_FETCHED](state, { settings }) {
    state.legacy.accountSettings = {
      ...state.legacy.accountSettings,
      ...settings,
    };
    _.merge(state, build(accountLegacyToState(settings)));
  },
  [USER_SETTINGS_FETCHED](state, { settings }) {
    state.legacy.userSettings = {
      ...state.legacy.userSettings,
      ...settings,
    };
  },
};

// initial state
const state = {
  legacy: {
    userSettings: {},
    accountSettings: {},
  },
  ...build(),
};

export default {
  namespaced: true,
  state,
  getters: {
    nestedLegacy(s) { return s.legacy.userSettings; },
  },
  actions,
  mutations,
};
